import React, { useCallback, useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";
import styles from "./ProgrammeExploreFuture.module.css";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

interface ExploreFutureProps {
  data: {
    id?: number;
    title?: string;
    hide_section?: boolean;
    items?: Array<{
      id: number;
      heading: string;
      sub_heading: string;
      url: string | null;
      image: {
        id: number;
        documentId: string;
        url: string;
        alternativeText: string | null;
      };
    }>;
    // Legacy structure support
    title_description?: {
      title: string;
      description: string;
    };
    learn_more_link_cta?: any;
    explore_your_future_cards?: Array<{
      id: number;
      link: {
        label: string;
        url: string;
      };
      image: {
        url: string;
        alternativeText?: any;
      };
    }>;
    mobile_img?: {
      url: string;
      id: number;
      documentId: string;
      alternativeText: string | null;
    };
  };
  popUpData?: any;
}

const ProgrammeExploreFuture: React.FC<ExploreFutureProps> = ({ data }) => {
  const swiperRef = useRef<any>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setIsMobile(window.innerWidth <= 760);
      }
    };
    if (typeof window !== "undefined") {
      handleResize();
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  useEffect(() => {
    let handleSlideChange: (() => void) | null = null;
    let swiperInstance: any = null;

    const updatePrevButton = () => {
      const swiper = swiperRef?.current?.swiper;
      if (swiper) {
        const arrow = document.getElementsByClassName(
          "swiper-button-prev"
        )[0] as HTMLElement;
        
        if (arrow) {
          const realIndex = swiper?.realIndex || 0;
          arrow.style.display = realIndex === 0 ? "none" : "block";
        }
      }
    };

    // Wait for swiper to be initialized
    const timer = setTimeout(() => {
      const swiper = swiperRef?.current?.swiper;
      if (swiper) {
        // Store swiper instance for cleanup
        swiperInstance = swiper;
        
        // Set initial state
        updatePrevButton();

        // Create handler function
        handleSlideChange = () => {
          updatePrevButton();
        };

        // Listen for slide changes
        swiper.on("slideChange", handleSlideChange);
        swiper.on("init", handleSlideChange);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (swiperInstance && handleSlideChange) {
        swiperInstance.off("slideChange", handleSlideChange);
        swiperInstance.off("init", handleSlideChange);
      }
    };
  }, []);

  const handleNextSlide = useCallback(
    () => swiperRef.current?.swiper?.slideNext(),
    []
  );
  const handlePrevSlide = useCallback(
    () => swiperRef.current?.swiper?.slidePrev(),
    []
  );
  
  // Hide section if hide_section is true
  if (data?.hide_section === true) {
    return null;
  }

  // Determine which data structure to use (new API or legacy)
  const isNewStructure = data?.items && data?.items.length > 0;
  const items = isNewStructure ? data.items : data?.explore_your_future_cards || [];

  // Don't render if there are no items
  if (!items || items.length === 0) {
    return null;
  }

  // Get title from new API structure or legacy structure
  const sectionTitle = data?.title || data?.title_description?.title;

  return (
    <div className={styles.talentedComponent} data-section="ExploreFuture">
      <div className={styles.talentedcontent}>
        {sectionTitle && (
          <div className={styles.titleSection}>
            <h2 className={styles.title}>{sectionTitle}</h2>
          </div>
        )}

        <div className={styles.rightSection}>
          <div className={styles.cardContainer}>
            <div
              className={styles.swiperNavigation}
              style={{ minHeight: "50px" }}
            >
              <Swiper
                ref={swiperRef}
                className={`${styles.swiperStyle} swiperStyle`}
                slidesPerView={3}
                spaceBetween={8}
                navigation={{
                  nextEl: ".swiper-button-next",
                  prevEl: ".swiper-button-prev",
                }}
                breakpoints={{
                  320: { slidesPerView: 1, spaceBetween: 10 },
                  480: { slidesPerView: 1, spaceBetween: 10 },
                  768: { slidesPerView: 1, spaceBetween: 20 },
                  960: { slidesPerView: 2.5, spaceBetween: 24 },
                  1540: { slidesPerView: 2.5, spaceBetween: 24 },
                }}
              >
                {items?.map((card: any, index: number) => (
                  <SwiperSlide key={card.id}>
                    <div className={styles.Schoolcard}>
                      {isNewStructure ? (
                        card?.url ? (
                          <Link
                            href={card.url}
                            aria-label={card?.heading}
                            shallow
                            title={card?.heading}
                          >
                            <div className={styles.imageWrapper}>
                              <Image
                                src={card?.image?.url}
                                alt={card?.image?.alternativeText || card?.heading || "Project card"}
                                title={card?.image?.alternativeText || card?.heading || "Project card"}
                                width={344}
                                height={448}
                                sizes="(max-width: 768px) 320px, 640px"
                                quality={80}
                                style={{
                                  objectFit: "cover",
                                  objectPosition: "center",
                                }}
                                className={styles.cardImage}
                                priority={index === 0}
                                loading={index === 0 ? "eager" : "lazy"}
                              />
                              <div className={styles.cardContentContainer}>
                                <div className={styles.cardContent}>
                                  <div>
                                    <p className={styles.cardText}>
                                      {card?.heading}
                                    </p>
                                    {card?.sub_heading && (
                                      <p className={styles.cardSubText}>
                                        {card?.sub_heading}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Link>
                        ) : (
                          <div className={styles.imageWrapper}>
                            <Image
                              src={card?.image?.url}
                              alt={card?.image?.alternativeText || card?.heading || "Project card"}
                              title={card?.image?.alternativeText || card?.heading || "Project card"}
                              width={344}
                              height={448}
                              sizes="(max-width: 768px) 320px, 640px"
                              quality={80}
                              style={{
                                objectFit: "cover",
                                objectPosition: "center",
                              }}
                              className={styles.cardImage}
                              priority={index === 0}
                              loading={index === 0 ? "eager" : "lazy"}
                            />
                            <div className={styles.cardContentContainer}>
                              <div className={styles.cardContent}>
                                <div>
                                  <p className={styles.cardText}>
                                    {card?.heading}
                                  </p>
                                  {card?.sub_heading && (
                                    <p className={styles.cardSubText}>
                                      {card?.sub_heading}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      ) : (
                        <Link
                          href={card?.link?.url}
                          aria-label={card?.link?.label}
                          shallow
                          title={card?.link?.label}
                        >
                          <div className={styles.imageWrapper}>
                            <Image
                              src={card?.image?.url}
                              alt={card?.image?.alternativeText || "School-cards"}
                              title={
                                card?.image?.alternativeText || "School-cards"
                              }
                              width={300}
                              height={390}
                              sizes="(max-width: 768px) 320px, 640px"
                              quality={80}
                              style={{
                                objectFit: "cover",
                                objectPosition: "center",
                              }}
                              className={styles.cardImage}
                              priority={index === 0}
                              loading={index === 0 ? "eager" : "lazy"}
                            />
                            <div className={styles.cardContentContainer}>
                              <div className={styles.cardContent}>
                                <p className={styles.cardText}>
                                  {card?.link?.label}
                                </p>
                                <ArrowRight className={styles.arrowIcon} />
                              </div>
                            </div>
                          </div>
                        </Link>
                      )}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {((isMobile && items.length > 1) || (!isMobile && items.length > 2)) && (
                <>
                  <button
                    aria-label="Next Slide"
                    className={`swiper-button-next ${styles.nextButton}`}
                    onClick={handleNextSlide}
                  ></button>

                  <button
                    aria-label="Previous Slide"
                    className={`swiper-button-prev ${styles.prevButton}`}
                    onClick={handlePrevSlide}
                  ></button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgrammeExploreFuture;
